import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Início', path: '/' },
    { name: 'Portfólio', path: '/portfolio' },
    { name: 'Serviços', path: '/servicos' },
    { name: 'Sobre', path: '/sobre' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contato', path: '/contato' },
  ];

  const isHeroPage = location.pathname === '/';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'glass-effect border-b border-gray-200 shadow-sm'
          : isHeroPage
          ? 'bg-transparent'
          : 'bg-white border-b border-gray-100'
      }`}
      data-testid="main-header"
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            className="text-2xl font-bold tracking-tight"
            data-testid="logo-link"
          >
            <span className={isScrolled || !isHeroPage ? 'text-gray-900' : 'text-white'}>Gran</span>
            <span className="text-[#FF3366]">Tour</span>
            <span className={isScrolled || !isHeroPage ? 'text-gray-900' : 'text-white'}>360</span>
          </Link>

          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-medium transition-colors hover:text-[#FF3366] ${
                  location.pathname === item.path
                    ? 'text-[#FF3366]'
                    : isScrolled || !isHeroPage
                    ? 'text-gray-600'
                    : 'text-white/90'
                }`}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          <Link
            to="/contato"
            className="hidden lg:block px-6 py-2.5 bg-[#FF3366] text-white text-sm font-semibold rounded-md hover:bg-[#FF5C85] transition-colors"
            data-testid="header-cta-button"
          >
            Solicitar Orçamento
          </Link>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 ${isScrolled || !isHeroPage ? 'text-gray-900' : 'text-white'}`}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-gray-200 shadow-lg"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-4 space-y-4">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block text-sm font-medium transition-colors ${
                    location.pathname === item.path
                      ? 'text-[#FF3366]'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                >
                  {item.name}
                </Link>
              ))}
              <Link
                to="/contato"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block px-6 py-2.5 bg-[#FF3366] text-white text-sm font-semibold rounded-md hover:bg-[#FF5C85] transition-colors text-center"
                data-testid="mobile-cta-button"
              >
                Solicitar Orçamento
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
